import {observer} from 'mobx-react-lite';
import React, {useContext, useEffect, useState} from 'react';
import {Header, INotificationProps, Main} from '../../../patterns';
import {StoreContext} from '../base/root-store';
import TemplateSignUp, {ITemplateSignUpProps} from '../../../patterns/templates/signup/signup';
import TemplateAgreement, {ITemplateAgreementProps} from '../../../patterns/templates/agreement/agreement';
import agreement from '../signup/agreement.md';
import logoMoticheck from '../assets/logo_moticheck_new.png';
import {GenericError} from '../base/GenericError';
import {DateRange} from 'react-day-picker';
import {formatToDayMonthAndYearWithDateInput} from '../base/dateUtils';

export const CompanySignUpPage = observer((): JSX.Element => {
    const store = useContext(StoreContext).companySignUpStore;
    const labelStore = store.labelStore;

    useEffect(() => {
        store.onEnter();
    }, []);

    if (store.state === 'error') {
        return <Main><GenericError/></Main>;
    }

    const notifications: INotificationProps[] = [];

    if (store.invalidEmail && store.stage === 'signup') {
        notifications.push(
            {
                type: 'info',
                text: labelStore.label('company-sign-up.email-exists'),
                defaultIsOpen: true,
            },
        );
    }

    const [companyName, setCompanyName] = useState<string>('');
    const [companyAddress, setCompanyAddress] = useState<string>('');
    const [companyRegNumber, setCompanyRegNumber] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [surveyStartDate, setSurveyStartDate] = useState<Date | DateRange | undefined>(undefined);

    const signUpStageProps = (): ITemplateSignUpProps => ({
        title: labelStore.label('company-sign-up.title'),
        textFields: [
            {
                label: labelStore.label('company-sign-up.company-name'),
                iconLeft: 'building',
                id: 'text1',
                name: 'field',
                type: 'text',
                value: companyName,
                onChange: (value): void => setCompanyName(value),
                invalid: store.showValidation && store.checkIfMissing(companyName) !== undefined,
            },
            {
                label: labelStore.label('company-sign-up.company-address'),
                iconLeft: 'ballpen',
                id: 'text2',
                name: 'field',
                type: 'text',
                value: companyAddress,
                onChange: (value): void => setCompanyAddress(value),
                description: store.showValidation && store.checkifMissingOrTooShort(companyAddress) !== undefined ? labelStore.label('common.validation.too-short-address') : undefined,
                invalid: store.showValidation && store.checkifMissingOrTooShort(companyAddress) !== undefined,
            },
            {
                label: labelStore.label('company-sign-up.reg-number'),
                iconLeft: 'building',
                id: 'text3',
                name: 'field',
                type: 'text',
                value: companyRegNumber,
                onChange: (value): void => setCompanyRegNumber(value),
                description: store.showValidation && store.checkifMissingOrTooShort(companyRegNumber) !== undefined ? labelStore.label('common.validation.too-short-number') : undefined,
                invalid: store.showValidation && store.checkifMissingOrTooShort(companyRegNumber) !== undefined,
            },
            {
                label: labelStore.label('company-sign-up.first-name'),
                iconLeft: 'user',
                id: 'text4',
                name: 'field',
                type: 'text',
                value: firstName,
                onChange: (value): void => setFirstName(value),
                invalid: store.showValidation && store.checkIfMissing(firstName) !== undefined,
            },
            {
                label: labelStore.label('company-sign-up.last-name'),
                iconLeft: 'user',
                id: 'text5',
                name: 'field',
                type: 'text',
                value: lastName,
                onChange: (value): void => setLastName(value),
                invalid: store.showValidation && store.checkIfMissing(lastName) !== undefined,
            },
            {
                label: labelStore.label('company-sign-up.email'),
                iconLeft: 'mail-closed',
                id: 'text6',
                name: 'field',
                type: 'email',
                value: email,
                invalid: store.showValidation && store.checkEmail(email) !== undefined,
                description: store.showValidation && store.checkEmail(email) !== undefined ? labelStore.label(`common.validation.${store.checkEmail(email)}`) : undefined,
                onChange: (value): void => setEmail(value),
            },
        ],
        calendarField: {
            field: {
                label: labelStore.label('company-sign-up.survey-start-date'),
                iconLeft: 'calendar-check',
                iconSize: 'small',
                isStatic: true,
                id: 'sign-up-calendar',
                name: 'sign-up-calendar',
                description: store.showValidation && store.checkStartDate(surveyStartDate as Date) !== undefined ? labelStore.label(`common.validation.${store.checkStartDate(surveyStartDate as Date)}`) : undefined,
                invalid: store.showValidation && store.checkStartDate(surveyStartDate as Date) !== undefined,
            },
            value: surveyStartDate as Date,
            onDateChange: (value: Date | DateRange | undefined): void => setSurveyStartDate(value as Date),
            type: undefined,
            format: 'dd.MM.yyyy',
        },
        tutorials: {label: '', link: <a href='https://www.youtube.com/watch?v=SSQtZyvRwSk'>{labelStore.label('company-sign-up.video-link')}</a>},
        button:
            {
                text: labelStore.label('company-sign-up.button-label'),
                onClick: async (): Promise<void> => {
                    await store.signUpCompany(companyName, companyAddress, companyRegNumber, surveyStartDate as Date, firstName, lastName, email);
                },
            },

        onSubmit: async (): Promise<void> => {
            await store.signUpCompany(companyName, companyAddress, companyRegNumber, surveyStartDate as Date, firstName, lastName, email);
        },
    });

    let filledAgreement: string;

    if (surveyStartDate !== undefined) {
        filledAgreement = agreement
            .replace('{{customer}}', companyName)
            .replaceAll('{{customerEmail}}', email)
            .replace('{{firstName}}', firstName)
            .replace('{{lastName}}', lastName)
            .replace('{{regNumber}}', companyRegNumber)
            .replace('{{address}}', companyAddress)
            .replaceAll('{{date}}', formatToDayMonthAndYearWithDateInput(surveyStartDate as Date));
    }

    const agreementStageProps = (): ITemplateAgreementProps => {
        const companyId = store.companyId;

        return ({
            title: labelStore.label('company-sign-up.agreement-title'),
            button: {
                text: labelStore.label('company-sign-up.agreement-button'),
                id: companyId,
                onClick: async (): Promise<void> => {
                    await store.finaliseSignUp(companyId);
                },
            },
            content: filledAgreement,
            onSubmit: async (): Promise<void> => {
                await store.finaliseSignUp(companyId);
            },
        });
    };

    return (
        <Main notifications={notifications} showBackground={true} isLoading={store.state === 'loading'}>
            <Header logo={{
                url: '#',
                image: logoMoticheck,
                title: 'Moticheck',
            }}/>
            {store.stage === 'signup' ?
                <TemplateSignUp{...(signUpStageProps())}/> :
                <TemplateAgreement{...(agreementStageProps())}/>
            }
        </Main>
    );
});
